/**
 * EditRecipeModal Component
 *
 * Lets operators adjust the as-mixed ingredient quantities for a single
 * substrate batch (MIXING or BAGGED). Modelled on ProductionCardModal but with
 * editable kg values. Saving re-reconciles inventory and recomputes dry weight
 * and cost server-side via the substrate-batches `update-recipe` action — the
 * shared recipe template is never modified.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useSubstrateMutations } from '@/hooks/useSubstrateBatches'
import { useBlockFormats } from '@/hooks/useSubstrateResources'
import { Loader2, AlertTriangle, Plus, Trash2 } from 'lucide-react'

function formatBagsLabel(qtyKg, conversionFactor, purchaseUnit) {
  const conv = Number(conversionFactor) || 0
  if (!purchaseUnit || conv <= 1 || qtyKg <= 0) return null
  const bags = qtyKg / conv
  const whole = Math.abs(bags - Math.round(bags)) < 0.01
  const bagText = whole ? `${Math.round(bags)}` : `~${bags.toFixed(1)}`
  return `${bagText} × ${conv}kg ${purchaseUnit}${Math.round(bags) === 1 ? '' : 's'}`
}

// Display a quantity without noisy trailing zeros (45 -> "45", 1.8 -> "1.8")
function formatQty(value) {
  const n = Number(value) || 0
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100)
}

// --- Safe arithmetic expression evaluation (no eval) ---------------------------
// Supports + - * / and parentheses on decimal numbers, e.g. "45.0 + 6" -> 51.
function tokenizeExpression(s) {
  const tokens = []
  let i = 0
  while (i < s.length) {
    const c = s[i]
    if (c === ' ') { i++; continue }
    if ('+-*/()'.includes(c)) { tokens.push(c); i++; continue }
    let j = i
    while (j < s.length && /[\d.]/.test(s[j])) j++
    const num = s.slice(i, j)
    if (!/^(\d+\.?\d*|\.\d+)$/.test(num)) throw new Error('bad number')
    tokens.push(parseFloat(num))
    i = j
  }
  return tokens
}

function toRPN(tokens) {
  const out = []
  const ops = []
  const prec = { 'u-': 3, '*': 2, '/': 2, '+': 1, '-': 1 }
  let prev = null // null | 'num' | 'op' | '(' | ')'
  for (const t of tokens) {
    if (typeof t === 'number') {
      out.push(t); prev = 'num'
    } else if (t === '(') {
      ops.push(t); prev = '('
    } else if (t === ')') {
      while (ops.length && ops[ops.length - 1] !== '(') out.push(ops.pop())
      if (ops.pop() !== '(') throw new Error('mismatched parens')
      prev = ')'
    } else {
      let op = t
      if ((op === '-' || op === '+') && (prev === null || prev === 'op' || prev === '(')) {
        if (op === '+') { prev = 'op'; continue } // unary plus is a no-op
        op = 'u-'
      }
      const p = prec[op]
      const rightAssoc = op === 'u-'
      while (ops.length) {
        const top = ops[ops.length - 1]
        if (top === '(') break
        const tp = prec[top]
        if (tp > p || (tp === p && !rightAssoc)) out.push(ops.pop())
        else break
      }
      ops.push(op); prev = 'op'
    }
  }
  while (ops.length) {
    const o = ops.pop()
    if (o === '(') throw new Error('mismatched parens')
    out.push(o)
  }
  return out
}

function evalRPN(rpn) {
  const st = []
  for (const t of rpn) {
    if (typeof t === 'number') { st.push(t); continue }
    if (t === 'u-') { const a = st.pop(); st.push(-a); continue }
    const b = st.pop()
    const a = st.pop()
    if (a === undefined || b === undefined) throw new Error('bad expression')
    st.push(t === '+' ? a + b : t === '-' ? a - b : t === '*' ? a * b : a / b)
  }
  if (st.length !== 1) throw new Error('bad expression')
  return st[0]
}

/**
 * Evaluate a numeric expression string. Returns a finite number, or null if the
 * input is empty or not a valid expression.
 */
function evaluateExpression(input) {
  if (input == null) return null
  const s = String(input).trim()
  if (s === '') return null
  if (!/^[\d+\-*/().\s]+$/.test(s)) return null
  try {
    const value = evalRPN(toRPN(tokenizeExpression(s)))
    return Number.isFinite(value) ? value : null
  } catch {
    return null
  }
}

/**
 * Inline, click-to-edit quantity cell that accepts a plain number or an
 * arithmetic expression (evaluated on Enter/blur). Selects all on focus so the
 * existing value can be replaced by typing.
 */
function ExpressionField({ value, onCommit }) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState('')
  const [invalid, setInvalid] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  const start = () => {
    setText(formatQty(value))
    setInvalid(false)
    setEditing(true)
  }

  const commit = () => {
    const result = evaluateExpression(text)
    if (result == null || result < 0) {
      setInvalid(true)
      return
    }
    setEditing(false)
    setInvalid(false)
    const rounded = Math.round(result * 10000) / 10000
    if (rounded !== Number(value)) onCommit(rounded)
  }

  const cancel = () => {
    setEditing(false)
    setInvalid(false)
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={start}
        className="inline-flex items-center gap-1 font-medium rounded-md px-2 py-1 -my-1 hover:bg-muted/50 transition-colors"
      >
        <span>{formatQty(value)}</span>
        <span className="text-muted-foreground">kg</span>
      </button>
    )
  }

  return (
    <div className="inline-flex items-center gap-1">
      <input
        ref={inputRef}
        type="text"
        inputMode="text"
        value={text}
        onChange={(e) => { setText(e.target.value); setInvalid(false) }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit() }
          else if (e.key === 'Escape') { e.preventDefault(); cancel() }
        }}
        onBlur={commit}
        className={`w-24 h-7 px-2 py-1 bg-white rounded-md shadow-sm outline outline-1 text-sm font-medium focus:outline-2 ${
          invalid ? 'outline-red-500 focus:outline-red-500' : 'outline-neutral-400 focus:outline-primary'
        }`}
      />
      <span className="text-sm text-muted-foreground">kg</span>
    </div>
  )
}

let bagRowSeq = 0
const nextBagKey = () => `bag-${bagRowSeq++}`

export default function EditRecipeModal({ isOpen, onClose, batch, onSaved }) {
  const { getBatchIngredients, updateRecipeIngredients } = useSubstrateMutations()
  const { blockFormats } = useBlockFormats()

  const [rows, setRows] = useState([])
  const [bagRows, setBagRows] = useState([])
  const [bagsDirty, setBagsDirty] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [saveError, setSaveError] = useState(null)
  const [warnings, setWarnings] = useState([])

  // Seed bag rows from the batch's existing fills when the modal opens
  useEffect(() => {
    if (!isOpen) return
    const fills = batch?.fills || []
    setBagRows(fills.map(f => ({
      key: nextBagKey(),
      block_format_id: f.block_format_id,
      quantity: String(f.quantity ?? '')
    })))
    setBagsDirty(false)
  }, [isOpen, batch?.id, batch?.fills])

  // Load the stored as-mixed ingredient rows when the modal opens
  useEffect(() => {
    if (!isOpen || !batch?.id) return
    let cancelled = false

    setIsFetching(true)
    setLoadError(null)
    setSaveError(null)
    setWarnings([])

    getBatchIngredients(batch.id)
      .then(data => {
        if (cancelled) return
        setRows((data || []).map(r => ({
          ...r,
          // Original recorded quantity, used for stock-availability maths
          original_quantity_kg: Number(r.actual_quantity_kg),
          actual_quantity_kg: Number(r.actual_quantity_kg)
        })))
      })
      .catch(err => {
        if (!cancelled) setLoadError(err.message)
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false)
      })

    return () => { cancelled = true }
  }, [isOpen, batch?.id, getBatchIngredients])

  const handleQuantityChange = useCallback((id, value) => {
    const qty = Math.max(0, Number(value) || 0)
    setRows(prev => prev.map(r => r.id === id ? { ...r, actual_quantity_kg: qty } : r))
  }, [])

  // Active bag formats only. is_bag excludes bare substrate fill formats such as
  // imported Shiitake Logs. Stock isn't reliably reflected for bags in the unified
  // inventory view, so we don't filter by stock (only annotate a positive count).
  const activeFormats = useMemo(
    () => (blockFormats || []).filter(f => f.active !== false && f.is_bag !== false),
    [blockFormats]
  )

  const addBagRow = useCallback(() => {
    setBagRows(prev => [...prev, { key: nextBagKey(), block_format_id: '', quantity: '' }])
    setBagsDirty(true)
  }, [])
  const updateBagRow = useCallback((key, patch) => {
    setBagRows(prev => prev.map(r => r.key === key ? { ...r, ...patch } : r))
    setBagsDirty(true)
  }, [])
  const removeBagRow = useCallback((key) => {
    setBagRows(prev => prev.filter(r => r.key !== key))
    setBagsDirty(true)
  }, [])

  const totals = useMemo(() => {
    let weight = 0
    let dryWeight = 0
    let cost = 0
    let hasCost = false
    for (const r of rows) {
      const qty = Number(r.actual_quantity_kg) || 0
      weight += qty
      dryWeight += qty * (1 - (Number(r.moisture_content) || 0))
      if (r.unit_cost_snapshot != null) {
        cost += qty * Number(r.unit_cost_snapshot)
        hasCost = true
      }
    }
    return { weight, dryWeight, cost, hasCost }
  }, [rows])

  // Stock warnings: available for this edit = current stock + what this batch
  // currently holds (reversal restores the batch's portion before re-deducting).
  const stockWarnings = useMemo(() => {
    return rows
      .filter(r => r.track_inventory && r.item_id && r.available_stock != null)
      .map(r => {
        const availableForEdit = Number(r.available_stock) + Number(r.original_quantity_kg)
        return Number(r.actual_quantity_kg) > availableForEdit + 0.0001
          ? { name: r.material_name, needed: Number(r.actual_quantity_kg), available: availableForEdit }
          : null
      })
      .filter(Boolean)
  }, [rows])

  const ingredientsDirty = useMemo(
    () => rows.some(r => Number(r.actual_quantity_kg) !== Number(r.original_quantity_kg)),
    [rows]
  )

  // A bag row is incomplete unless it has both a format and a count >= 1
  const bagsValid = useMemo(
    () => bagRows.every(r => r.block_format_id && Number(r.quantity) >= 1),
    [bagRows]
  )

  const isDirty = ingredientsDirty || bagsDirty

  const handleSave = async () => {
    setIsSaving(true)
    setSaveError(null)
    setWarnings([])
    try {
      const payload = {
        id: batch.id,
        ingredients: rows.map(r => ({
          item_id: r.item_id ?? null,
          material_name: r.material_name,
          track_inventory: !!r.track_inventory,
          actual_quantity_kg: Number(r.actual_quantity_kg)
        }))
      }
      // Only send fills when the bag section was touched, so untouched batches
      // aren't accidentally reset.
      if (bagsDirty) {
        payload.fills = bagRows
          .filter(r => r.block_format_id && Number(r.quantity) >= 1)
          .map(r => ({ block_format_id: r.block_format_id, quantity: Number(r.quantity) }))
      }
      const response = await updateRecipeIngredients(payload)
      if (response?.warnings?.length) {
        setWarnings(response.warnings)
      }
      onSaved?.()
    } catch (err) {
      setSaveError(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Recipe</DialogTitle>
          <DialogDescription>
            Adjust ingredient quantities for {batch?.batch_code}. This changes
            only this batch — the recipe template is unchanged.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-content-secondary">Recipe</span>
              <span className="text-sm font-medium">{batch?.recipe?.name || '-'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-content-secondary">Total Weight</span>
              <span className="text-sm font-medium">{totals.weight.toFixed(1)} kg</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-content-secondary">Dry Weight</span>
              <span className="text-sm font-medium">{totals.dryWeight.toFixed(1)} kg</span>
            </div>
            {totals.hasCost && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-content-secondary">Est. Material Cost</span>
                <span className="text-sm font-medium">£{totals.cost.toFixed(2)}</span>
              </div>
            )}
          </div>

          {loadError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{loadError}</AlertDescription>
            </Alert>
          )}

          {isFetching ? (
            <div className="flex items-center justify-center py-8 text-content-secondary">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading ingredients…
            </div>
          ) : (
            rows.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Ingredients</h4>
                <div className="rounded-md border divide-y">
                  {rows.map(r => {
                    const qty = Number(r.actual_quantity_kg) || 0
                    const bagsLabel = formatBagsLabel(qty, r.conversion_factor, r.purchase_unit)
                    return (
                      <div key={r.id} className="flex items-center justify-between px-3 py-2 text-sm gap-2">
                        <div className="min-w-0">
                          <span>{r.material_name}</span>
                          {bagsLabel && (
                            <div className="text-xs text-content-tertiary">{bagsLabel}</div>
                          )}
                        </div>
                        <div className="shrink-0">
                          <ExpressionField
                            value={qty}
                            onCommit={(val) => handleQuantityChange(r.id, val)}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          )}

          {!isFetching && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">
                  Bags <span className="text-xs font-normal text-content-tertiary">(optional)</span>
                </h4>
                <Button type="button" variant="ghost" size="sm" onClick={addBagRow} className="h-7 px-2">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {bagRows.length === 0 ? (
                <p className="text-xs text-content-tertiary">
                  Record how many bags were filled now, or leave it for the inoculation step.
                </p>
              ) : (
                <div className="space-y-2">
                  {bagRows.map(row => {
                    const usedElsewhere = new Set(
                      bagRows.filter(r => r.key !== row.key && r.block_format_id).map(r => r.block_format_id)
                    )
                    const options = activeFormats.filter(f => !usedElsewhere.has(f.id))
                    return (
                      <div key={row.key} className="flex items-center gap-2">
                        <Select
                          value={row.block_format_id}
                          onValueChange={(v) => updateBagRow(row.key, { block_format_id: v })}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Bag format" />
                          </SelectTrigger>
                          <SelectContent>
                            {options.length === 0 ? (
                              <SelectItem value="__none" disabled>No active bag formats</SelectItem>
                            ) : (
                              options.map(f => (
                                <SelectItem key={f.id} value={f.id}>
                                  {f.name} ({f.weight_kg}kg)
                                  {Number(f.current_inventory) > 0 ? ` · ${f.current_inventory} in stock` : ''}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          min="1"
                          value={row.quantity}
                          onChange={(e) => updateBagRow(row.key, { quantity: e.target.value })}
                          placeholder="Bags"
                          className="w-24"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeBagRow(row.key)}
                          className="h-9 px-2"
                        >
                          <Trash2 className="h-4 w-4 text-odin-red" />
                        </Button>
                      </div>
                    )
                  })}
                  <div className="flex items-center justify-between text-xs text-content-secondary px-1">
                    <span>Total bags</span>
                    <span className="font-medium">
                      {bagRows.reduce((s, r) => s + (Number(r.quantity) || 0), 0)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {stockWarnings.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <p className="font-medium mb-1">Not enough stock for:</p>
                <ul className="list-disc list-inside text-xs space-y-0.5">
                  {stockWarnings.map(w => (
                    <li key={w.name}>
                      {w.name}: need {w.needed.toFixed(1)} kg, {w.available.toFixed(1)} kg available
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {warnings.length > 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc list-inside text-xs space-y-0.5">
                  {warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {saveError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{saveError}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isFetching || !isDirty || !bagsValid}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              'Save Recipe'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
